import { createDocument, getDocument, getCollection, setDocument } from './firestore.service'
import { clearCollection } from './dataManagement.service'

const SETTINGS_COLLECTION = 'auditLogSettings'
const SETTINGS_DOC_ID = 'main'
const LOGS_COLLECTION = 'auditLogs'

export const AUDIT_LOG_LEVELS = ['critical', 'error', 'warning', 'info']
export const AUDIT_LOG_CATEGORIES = ['access', 'auth', 'notifications', 'payments', 'orders', 'sales', 'stock', 'system']

const getDefaultSettings = () => ({
  enabled: true,
  levels: {
    critical: true,
    error: true,
    warning: true,
    info: false
  },
  categories: {
    access: true,
    auth: true,
    notifications: true,
    payments: true,
    orders: true,
    sales: true,
    stock: true,
    system: true
  },
  updatedAt: new Date()
})

const normalizeSettings = (raw) => {
  const defaults = getDefaultSettings()
  const normalized = {
    enabled: raw?.enabled !== false,
    levels: { ...defaults.levels, ...(raw?.levels || {}) },
    categories: { ...defaults.categories, ...(raw?.categories || {}) },
    updatedAt: raw?.updatedAt || defaults.updatedAt
  }
  return normalized
}

let cachedSettings = null
let lastSettingsFetch = 0
const SETTINGS_TTL = 30000

const loadSettings = async (force = false) => {
  const now = Date.now()
  if (!force && cachedSettings && now - lastSettingsFetch < SETTINGS_TTL) {
    return cachedSettings
  }
  let data = null
  try {
    data = await getDocument(SETTINGS_COLLECTION, SETTINGS_DOC_ID)
  } catch (_error) {
    data = null
  }
  cachedSettings = normalizeSettings(data || {})
  lastSettingsFetch = now
  return cachedSettings
}

export const getAuditLogSettings = async () => {
  return await loadSettings(true)
}

export const saveAuditLogSettings = async (settings) => {
  const normalized = normalizeSettings(settings || {})
  await setDocument(SETTINGS_COLLECTION, SETTINGS_DOC_ID, normalized, { merge: true })
  cachedSettings = normalized
  lastSettingsFetch = Date.now()
  return normalized
}

const getSafeString = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch (_error) {
    return String(value)
  }
}

const normalizeMessage = (message, fallback) => {
  const raw = getSafeString(message).trim()
  return raw || fallback
}

const normalizeError = (error) => {
  if (!error) return null
  const message = error?.message || getSafeString(error)
  return {
    message: getSafeString(message),
    stack: error?.stack || null,
    name: error?.name || null
  }
}

const buildSourceInfo = (extra = {}) => {
  if (typeof window === 'undefined') {
    return { ...extra }
  }
  return {
    path: window.location?.pathname || null,
    fullPath: window.location?.href || null,
    userAgent: navigator?.userAgent || null,
    ...extra
  }
}

let currentUserContext = null

export const setAuditLoggerUser = (payload) => {
  currentUserContext = payload || null
}

const buildUserInfo = () => {
  const authUser = currentUserContext?.currentUser
  const userData = currentUserContext?.userData
  if (!authUser && !userData) return null
  return {
    uid: authUser?.uid || null,
    email: authUser?.email || userData?.email || null,
    displayName: userData?.displayName || authUser?.displayName || null,
    role: userData?.role || null
  }
}

const shouldLogEvent = (settings, level, category) => {
  if (!settings?.enabled) return false
  const levels = settings?.levels || {}
  const categories = settings?.categories || {}
  if (level && levels[level] === false) return false
  if (category && categories[category] === false) return false
  return true
}

export const logAuditEvent = async ({
  level = 'info',
  category = 'system',
  action = null,
  message = null,
  metadata = null,
  error = null,
  source = null
} = {}) => {
  const settings = await loadSettings()
  if (!shouldLogEvent(settings, level, category)) return null
  const payload = {
    level,
    category,
    action,
    message: normalizeMessage(message, action || 'Evento'),
    metadata: metadata || null,
    error: normalizeError(error),
    user: buildUserInfo(),
    source: buildSourceInfo(source || {})
  }
  try {
    return await createDocument(LOGS_COLLECTION, payload)
  } catch (_error) {
    return null
  }
}

export const getAuditLogs = async ({ limit = 200 } = {}) => {
  return await getCollection(LOGS_COLLECTION, [], 'createdAt', 'desc', limit)
}

const formatCsvValue = (value) => {
  if (value === null || value === undefined) return ''
  const raw = typeof value === 'string' ? value : getSafeString(value)
  const escaped = raw.replace(/"/g, '""')
  return `"${escaped}"`
}

const formatLogTimestamp = (value) => {
  if (!value) return ''
  if (typeof value.toDate === 'function') return value.toDate().toISOString()
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000).toISOString()
  if (value instanceof Date) return value.toISOString()
  const parsed = new Date(value)
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString()
  return ''
}

export const buildAuditLogsCsv = (logs = []) => {
  const headers = [
    'id',
    'createdAt',
    'level',
    'category',
    'action',
    'message',
    'user',
    'metadata',
    'error',
    'source'
  ]
  const rows = logs.map((log) => {
    const userLabel = log?.user?.displayName || log?.user?.email || log?.user?.uid || ''
    return [
      formatCsvValue(log?.id || ''),
      formatCsvValue(formatLogTimestamp(log?.createdAt)),
      formatCsvValue(log?.level || ''),
      formatCsvValue(log?.category || ''),
      formatCsvValue(log?.action || ''),
      formatCsvValue(log?.message || ''),
      formatCsvValue(userLabel),
      formatCsvValue(log?.metadata || {}),
      formatCsvValue(log?.error || {}),
      formatCsvValue(log?.source || {})
    ].join(',')
  })
  return [headers.join(','), ...rows].join('\n')
}

export const downloadAuditLogsCsv = (logs = [], filename = 'audit-logs.csv') => {
  if (typeof window === 'undefined') return ''
  const csv = buildAuditLogsCsv(logs)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
  return csv
}

export const clearAuditLogs = async () => {
  return await clearCollection(LOGS_COLLECTION)
}

let loggerInitialized = false
let isLogging = false

const logFromConsole = async (level, args) => {
  if (isLogging) return
  isLogging = true
  const message = args && args.length ? getSafeString(args[0]) : ''
  const metadata = args && args.length > 1 ? { args: args.slice(1).map(getSafeString) } : null
  await logAuditEvent({
    level,
    category: 'system',
    action: `console_${level}`,
    message,
    metadata
  })
  isLogging = false
}

export const initAuditLogger = () => {
  if (loggerInitialized || typeof window === 'undefined') return
  loggerInitialized = true
  const originalError = console.error
  const originalWarn = console.warn

  console.error = (...args) => {
    originalError(...args)
    logFromConsole('error', args)
  }

  console.warn = (...args) => {
    originalWarn(...args)
    logFromConsole('warning', args)
  }

  window.addEventListener('error', (event) => {
    logAuditEvent({
      level: 'critical',
      category: 'system',
      action: 'window_error',
      message: event?.message,
      error: event?.error,
      metadata: {
        filename: event?.filename || null,
        lineno: event?.lineno || null,
        colno: event?.colno || null
      }
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    logAuditEvent({
      level: 'critical',
      category: 'system',
      action: 'unhandled_rejection',
      message: event?.reason?.message || getSafeString(event?.reason),
      error: event?.reason
    })
  })
}
